import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useContent } from "@/lib/content-context";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import ImagePicker from "@/components/admin/ImagePicker";
import { TextField, TextArea, SaveBar, SectionCard, PageHeader } from "@/components/admin/Form";

export default function PagesEditor() {
    const { content, refresh } = useContent();
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => { if (content) setData(JSON.parse(JSON.stringify(content))); }, [content]);
    if (!data) return null;

    const set = (path, value) => {
        setData((prev) => {
            const next = JSON.parse(JSON.stringify(prev));
            const keys = path.split(".");
            let cur = next;
            for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
            cur[keys[keys.length - 1]] = value;
            return next;
        });
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/content", { data: {
                about: data.about,
                partner: data.partner,
                contact: data.contact,
                privacy: data.privacy,
                terms: data.terms,
                careers: data.careers,
                our_cafes: data.our_cafes,
            }});
            await refresh();
            toast.success("Pages saved.");
        } catch {
            toast.error("Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="pages-editor">
            <PageHeader subtitle="Pages" title="Edit About · Partner · Contact" />

            <SectionCard title="About Page">
                <ImagePicker label="Hero Image" value={data.about?.hero_image} onChange={(v) => set("about.hero_image", v)} testid="about-hero" hint="Recommended: 800 × 1000 px (4:5 portrait) · JPG · under 500 KB" />
                <TextField label="Title" value={data.about?.title} onChange={(v) => set("about.title", v)} testid="about-title-input" />
                <TextArea label="Intro" value={data.about?.intro} onChange={(v) => set("about.intro", v)} testid="about-intro" />
                <div className="border-t border-coffee/10 pt-6">
                    <div className="tracked text-coffee/60 text-[10px] mb-4">Sections</div>
                    {data.about?.sections?.map((s, i) => (
                        <div key={i} className="p-4 border border-coffee/10 mb-4 space-y-3">
                            <TextField label="Heading" value={s.heading} onChange={(v) => {
                                const arr = [...data.about.sections]; arr[i] = { ...arr[i], heading: v };
                                set("about.sections", arr);
                            }} testid={`about-sec-${i}-heading`} />
                            <TextArea label="Body" value={s.body} onChange={(v) => {
                                const arr = [...data.about.sections]; arr[i] = { ...arr[i], body: v };
                                set("about.sections", arr);
                            }} testid={`about-sec-${i}-body`} />
                            <button onClick={() => set("about.sections", data.about.sections.filter((_, idx) => idx !== i))} className="text-destructive text-[10px] tracked">
                                <Trash2 size={12} className="inline" /> Remove
                            </button>
                        </div>
                    ))}
                    <button onClick={() => set("about.sections", [...(data.about?.sections || []), { heading: "", body: "" }])} className="inline-flex items-center gap-2 tracked text-[10px] text-coffee gold-underline">
                        <Plus size={14} /> Add Section
                    </button>
                </div>

                {/* Philosophy */}
                <div className="border-t border-coffee/10 pt-6 mt-6">
                    <div className="tracked text-coffee/60 text-[10px] mb-4">Brand Philosophy (dark section)</div>
                    <TextField label="Eyebrow Label" value={data.about?.philosophy?.label} onChange={(v) => set("about.philosophy.label", v)} testid="phil-label" />
                    <TextArea label="Heading" value={data.about?.philosophy?.heading} onChange={(v) => set("about.philosophy.heading", v)} testid="phil-heading" />
                    <TextField label="Watermark Word" value={data.about?.philosophy?.watermark} onChange={(v) => set("about.philosophy.watermark", v)} testid="phil-watermark" />
                    <TextField label="Watermark Eyebrow" value={data.about?.philosophy?.watermark_eyebrow} onChange={(v) => set("about.philosophy.watermark_eyebrow", v)} testid="phil-watermark-eyebrow" />
                    <div className="mt-4">
                        <div className="tracked text-coffee/60 text-[10px] mb-3">Cards · icon options: Coffee, Home, Users, Sparkles, Heart, BookOpen, Leaf, Sun, Award, Compass</div>
                        {(data.about?.philosophy?.cards || []).map((c, i) => (
                            <div key={i} className="p-4 border border-coffee/10 mb-4 space-y-3">
                                <TextField label="Icon" value={c.icon} onChange={(v) => {
                                    const arr = [...data.about.philosophy.cards]; arr[i] = { ...arr[i], icon: v };
                                    set("about.philosophy.cards", arr);
                                }} testid={`phil-card-${i}-icon`} />
                                <TextField label="Title" value={c.title} onChange={(v) => {
                                    const arr = [...data.about.philosophy.cards]; arr[i] = { ...arr[i], title: v };
                                    set("about.philosophy.cards", arr);
                                }} testid={`phil-card-${i}-title`} />
                                <TextArea label="Body" value={c.body} onChange={(v) => {
                                    const arr = [...data.about.philosophy.cards]; arr[i] = { ...arr[i], body: v };
                                    set("about.philosophy.cards", arr);
                                }} testid={`phil-card-${i}-body`} />
                                <button onClick={() => set("about.philosophy.cards", data.about.philosophy.cards.filter((_, idx) => idx !== i))} className="text-destructive text-[10px] tracked">
                                    <Trash2 size={12} className="inline" /> Remove
                                </button>
                            </div>
                        ))}
                        <button onClick={() => set("about.philosophy.cards", [...(data.about?.philosophy?.cards || []), { icon: "Sparkles", title: "", body: "" }])} className="inline-flex items-center gap-2 tracked text-[10px] text-coffee gold-underline">
                            <Plus size={14} /> Add Card
                        </button>
                    </div>
                </div>

                {/* Founder */}
                <div className="border-t border-coffee/10 pt-6 mt-6">
                    <div className="tracked text-coffee/60 text-[10px] mb-4">Founder</div>
                    <TextField label="Eyebrow Label" value={data.about?.founder?.label} onChange={(v) => set("about.founder.label", v)} testid="founder-label" />
                    <ImagePicker label="Portrait" value={data.about?.founder?.image} onChange={(v) => set("about.founder.image", v)} testid="founder-image" hint="Recommended: 800 × 1000 px (4:5 portrait) · JPG · under 500 KB" />
                    <TextField label="Name" value={data.about?.founder?.name} onChange={(v) => set("about.founder.name", v)} testid="founder-name" />
                    <TextField label="Role" value={data.about?.founder?.role} onChange={(v) => set("about.founder.role", v)} testid="founder-role" />
                    <TextArea label="Pull Quote" value={data.about?.founder?.quote} onChange={(v) => set("about.founder.quote", v)} testid="founder-quote" />
                    <div className="mt-4">
                        <div className="tracked text-coffee/60 text-[10px] mb-3">Paragraphs</div>
                        {(data.about?.founder?.paragraphs || []).map((p, i) => (
                            <div key={i} className="p-4 border border-coffee/10 mb-4 space-y-3">
                                <TextArea label={`Paragraph ${i + 1}`} value={p} onChange={(v) => {
                                    const arr = [...data.about.founder.paragraphs]; arr[i] = v;
                                    set("about.founder.paragraphs", arr);
                                }} testid={`founder-para-${i}`} />
                                <button onClick={() => set("about.founder.paragraphs", data.about.founder.paragraphs.filter((_, idx) => idx !== i))} className="text-destructive text-[10px] tracked">
                                    <Trash2 size={12} className="inline" /> Remove
                                </button>
                            </div>
                        ))}
                        <button onClick={() => set("about.founder.paragraphs", [...(data.about?.founder?.paragraphs || []), ""])} className="inline-flex items-center gap-2 tracked text-[10px] text-coffee gold-underline">
                            <Plus size={14} /> Add Paragraph
                        </button>
                    </div>
                    <TextField label="Signoff (italic gold)" value={data.about?.founder?.signoff} onChange={(v) => set("about.founder.signoff", v)} testid="founder-signoff" />
                </div>

                {/* Team */}
                <div className="border-t border-coffee/10 pt-6 mt-6">
                    <div className="tracked text-coffee/60 text-[10px] mb-4">Team</div>
                    <TextField label="Eyebrow Label" value={data.about?.team?.label} onChange={(v) => set("about.team.label", v)} testid="team-label" />
                    <TextArea label="Heading" value={data.about?.team?.heading} onChange={(v) => set("about.team.heading", v)} testid="team-heading" />
                    <div className="mt-4">
                        <div className="tracked text-coffee/60 text-[10px] mb-3">Members</div>
                        {(data.about?.team?.members || []).map((m, i) => (
                            <div key={i} className="p-4 border border-coffee/10 mb-4 space-y-3">
                                <ImagePicker label="Square Photo" value={m.image} onChange={(v) => {
                                    const arr = [...data.about.team.members]; arr[i] = { ...arr[i], image: v };
                                    set("about.team.members", arr);
                                }} testid={`team-member-${i}-image`} hint="Recommended: 800 × 800 px (1:1 square) · JPG · under 400 KB" />
                                <TextField label="Name" value={m.name} onChange={(v) => {
                                    const arr = [...data.about.team.members]; arr[i] = { ...arr[i], name: v };
                                    set("about.team.members", arr);
                                }} testid={`team-member-${i}-name`} />
                                <TextField label="Role" value={m.role} onChange={(v) => {
                                    const arr = [...data.about.team.members]; arr[i] = { ...arr[i], role: v };
                                    set("about.team.members", arr);
                                }} testid={`team-member-${i}-role`} />
                                <TextField label="Personality Quote" value={m.quote} onChange={(v) => {
                                    const arr = [...data.about.team.members]; arr[i] = { ...arr[i], quote: v };
                                    set("about.team.members", arr);
                                }} testid={`team-member-${i}-quote`} />
                                <button onClick={() => set("about.team.members", data.about.team.members.filter((_, idx) => idx !== i))} className="text-destructive text-[10px] tracked">
                                    <Trash2 size={12} className="inline" /> Remove
                                </button>
                            </div>
                        ))}
                        <button onClick={() => set("about.team.members", [...(data.about?.team?.members || []), { name: "", role: "", quote: "", image: "" }])} className="inline-flex items-center gap-2 tracked text-[10px] text-coffee gold-underline">
                            <Plus size={14} /> Add Member
                        </button>
                    </div>
                </div>

                {/* Video close */}
                <div className="border-t border-coffee/10 pt-6 mt-6">
                    <div className="tracked text-coffee/60 text-[10px] mb-4">Closing Video Section</div>
                    <TextField label="Eyebrow Label" value={data.about?.video_section?.label} onChange={(v) => set("about.video_section.label", v)} testid="video-label" />
                    <TextArea label="Heading" value={data.about?.video_section?.heading} onChange={(v) => set("about.video_section.heading", v)} testid="video-heading" />
                    <TextArea label="Subtitle" value={data.about?.video_section?.subtitle} onChange={(v) => set("about.video_section.subtitle", v)} testid="video-subtitle" />
                    <TextField label="YouTube Video ID (e.g. dQw4w9WgXcQ — the part after watch?v=)" value={data.about?.video_section?.youtube_id} onChange={(v) => set("about.video_section.youtube_id", v)} testid="video-youtube-id" />
                </div>
            </SectionCard>

            <SectionCard title="Partner Page">
                <ImagePicker label="Hero Image" value={data.partner?.hero_image} onChange={(v) => set("partner.hero_image", v)} testid="partner-hero" hint="Recommended: 800 × 1000 px (4:5 portrait) · JPG · under 500 KB" />
                <TextField label="Title" value={data.partner?.title} onChange={(v) => set("partner.title", v)} testid="partner-title-input" />
                <TextArea label="Intro" value={data.partner?.intro} onChange={(v) => set("partner.intro", v)} testid="partner-intro" />
                <TextField label="Form Heading" value={data.partner?.form_heading} onChange={(v) => set("partner.form_heading", v)} testid="partner-form-heading" />
                <div className="border-t border-coffee/10 pt-6">
                    <div className="tracked text-coffee/60 text-[10px] mb-4">Benefits</div>
                    {data.partner?.benefits?.map((b, i) => (
                        <div key={i} className="p-4 border border-coffee/10 mb-4 space-y-3">
                            <TextField label="Heading" value={b.heading} onChange={(v) => {
                                const arr = [...data.partner.benefits]; arr[i] = { ...arr[i], heading: v };
                                set("partner.benefits", arr);
                            }} testid={`partner-ben-${i}-heading`} />
                            <TextArea label="Body" value={b.body} onChange={(v) => {
                                const arr = [...data.partner.benefits]; arr[i] = { ...arr[i], body: v };
                                set("partner.benefits", arr);
                            }} testid={`partner-ben-${i}-body`} />
                            <button onClick={() => set("partner.benefits", data.partner.benefits.filter((_, idx) => idx !== i))} className="text-destructive text-[10px] tracked">
                                <Trash2 size={12} className="inline" /> Remove
                            </button>
                        </div>
                    ))}
                    <button onClick={() => set("partner.benefits", [...(data.partner?.benefits || []), { heading: "", body: "" }])} className="inline-flex items-center gap-2 tracked text-[10px] text-coffee gold-underline">
                        <Plus size={14} /> Add Benefit
                    </button>
                </div>
            </SectionCard>

            <SectionCard title="Contact Page">
                <ImagePicker label="Hero Image" value={data.contact?.hero_image} onChange={(v) => set("contact.hero_image", v)} testid="contact-hero" hint="Recommended: 800 × 1000 px (4:5 portrait) · JPG · under 500 KB" />
                <TextField label="Title" value={data.contact?.title} onChange={(v) => set("contact.title", v)} testid="contact-title-input" />
                <TextArea label="Intro" value={data.contact?.intro} onChange={(v) => set("contact.intro", v)} testid="contact-intro" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <TextField label="Email" value={data.contact?.email} onChange={(v) => set("contact.email", v)} testid="contact-email-input" />
                    <TextField label="Phone" value={data.contact?.phone} onChange={(v) => set("contact.phone", v)} testid="contact-phone-input" />
                </div>
                <div className="border-t border-coffee/10 pt-6">
                    <div className="tracked text-coffee/60 text-[10px] mb-4">Locations</div>
                    {data.contact?.addresses?.map((a, i) => (
                        <div key={i} className="p-4 border border-coffee/10 mb-4 space-y-3">
                            <TextField label="City" value={a.city} onChange={(v) => {
                                const arr = [...data.contact.addresses]; arr[i] = { ...arr[i], city: v };
                                set("contact.addresses", arr);
                            }} testid={`contact-addr-${i}-city`} />
                            <TextField label="Address" value={a.line} onChange={(v) => {
                                const arr = [...data.contact.addresses]; arr[i] = { ...arr[i], line: v };
                                set("contact.addresses", arr);
                            }} testid={`contact-addr-${i}-line`} />
                            <TextField label="Hours" value={a.hours} onChange={(v) => {
                                const arr = [...data.contact.addresses]; arr[i] = { ...arr[i], hours: v };
                                set("contact.addresses", arr);
                            }} testid={`contact-addr-${i}-hours`} />
                            <button onClick={() => set("contact.addresses", data.contact.addresses.filter((_, idx) => idx !== i))} className="text-destructive text-[10px] tracked">
                                <Trash2 size={12} className="inline" /> Remove
                            </button>
                        </div>
                    ))}
                    <button onClick={() => set("contact.addresses", [...(data.contact?.addresses || []), { city: "", line: "", hours: "" }])} className="inline-flex items-center gap-2 tracked text-[10px] text-coffee gold-underline">
                        <Plus size={14} /> Add Location
                    </button>
                </div>
            </SectionCard>

            <SectionCard title="Privacy Policy Page">
                <TextField label="Eyebrow" value={data.privacy?.eyebrow} onChange={(v) => set("privacy.eyebrow", v)} testid="privacy-eyebrow" />
                <TextField label="Title" value={data.privacy?.title} onChange={(v) => set("privacy.title", v)} testid="privacy-title-input" />
                <TextField label="Last Updated" value={data.privacy?.updated} onChange={(v) => set("privacy.updated", v)} testid="privacy-updated" />
                <div className="border-t border-coffee/10 pt-6">
                    <div className="tracked text-coffee/60 text-[10px] mb-4">Clauses</div>
                    {data.privacy?.sections?.map((s, i) => (
                        <div key={i} className="p-4 border border-coffee/10 mb-4 space-y-3">
                            <TextField label="Heading" value={s.h} onChange={(v) => {
                                const arr = [...data.privacy.sections]; arr[i] = { ...arr[i], h: v };
                                set("privacy.sections", arr);
                            }} testid={`privacy-sec-${i}-h`} />
                            <TextArea label="Body" value={s.body} onChange={(v) => {
                                const arr = [...data.privacy.sections]; arr[i] = { ...arr[i], body: v };
                                set("privacy.sections", arr);
                            }} testid={`privacy-sec-${i}-body`} />
                            <button onClick={() => set("privacy.sections", data.privacy.sections.filter((_, idx) => idx !== i))} className="text-destructive text-[10px] tracked">
                                <Trash2 size={12} className="inline" /> Remove
                            </button>
                        </div>
                    ))}
                    <button onClick={() => set("privacy.sections", [...(data.privacy?.sections || []), { h: "", body: "" }])} className="inline-flex items-center gap-2 tracked text-[10px] text-coffee gold-underline">
                        <Plus size={14} /> Add Clause
                    </button>
                </div>
            </SectionCard>

            <SectionCard title="Terms & Conditions Page">
                <TextField label="Eyebrow" value={data.terms?.eyebrow} onChange={(v) => set("terms.eyebrow", v)} testid="terms-eyebrow" />
                <TextField label="Title" value={data.terms?.title} onChange={(v) => set("terms.title", v)} testid="terms-title-input" />
                <TextField label="Last Updated" value={data.terms?.updated} onChange={(v) => set("terms.updated", v)} testid="terms-updated" />
                <div className="border-t border-coffee/10 pt-6">
                    <div className="tracked text-coffee/60 text-[10px] mb-4">Clauses</div>
                    {data.terms?.sections?.map((s, i) => (
                        <div key={i} className="p-4 border border-coffee/10 mb-4 space-y-3">
                            <TextField label="Heading" value={s.h} onChange={(v) => {
                                const arr = [...data.terms.sections]; arr[i] = { ...arr[i], h: v };
                                set("terms.sections", arr);
                            }} testid={`terms-sec-${i}-h`} />
                            <TextArea label="Body" value={s.body} onChange={(v) => {
                                const arr = [...data.terms.sections]; arr[i] = { ...arr[i], body: v };
                                set("terms.sections", arr);
                            }} testid={`terms-sec-${i}-body`} />
                            <button onClick={() => set("terms.sections", data.terms.sections.filter((_, idx) => idx !== i))} className="text-destructive text-[10px] tracked">
                                <Trash2 size={12} className="inline" /> Remove
                            </button>
                        </div>
                    ))}
                    <button onClick={() => set("terms.sections", [...(data.terms?.sections || []), { h: "", body: "" }])} className="inline-flex items-center gap-2 tracked text-[10px] text-coffee gold-underline">
                        <Plus size={14} /> Add Clause
                    </button>
                </div>
            </SectionCard>

            <SectionCard title="Careers Page">
                <TextField label="Eyebrow" value={data.careers?.eyebrow} onChange={(v) => set("careers.eyebrow", v)} testid="careers-eyebrow" />
                <TextField label="Title" value={data.careers?.title} onChange={(v) => set("careers.title", v)} testid="careers-title-input" />
                <TextArea label="Intro" value={data.careers?.intro} onChange={(v) => set("careers.intro", v)} testid="careers-intro" />
                <TextField label="Contact Email" value={data.careers?.contact_email} onChange={(v) => set("careers.contact_email", v)} testid="careers-contact-email" />
                <TextField label="Roles Heading" value={data.careers?.roles_heading} onChange={(v) => set("careers.roles_heading", v)} testid="careers-roles-heading" />
                <TextField label="Form Heading" value={data.careers?.form_heading} onChange={(v) => set("careers.form_heading", v)} testid="careers-form-heading" />

                <div className="border-t border-coffee/10 pt-6">
                    <div className="tracked text-coffee/60 text-[10px] mb-4">Values · icon options: Coffee, Heart, Users, Sparkles, BookOpen, Home</div>
                    {data.careers?.values?.map((v, i) => (
                        <div key={i} className="p-4 border border-coffee/10 mb-4 space-y-3">
                            <TextField label="Icon" value={v.icon} onChange={(val) => {
                                const arr = [...data.careers.values]; arr[i] = { ...arr[i], icon: val };
                                set("careers.values", arr);
                            }} testid={`careers-val-${i}-icon`} />
                            <TextField label="Label" value={v.label} onChange={(val) => {
                                const arr = [...data.careers.values]; arr[i] = { ...arr[i], label: val };
                                set("careers.values", arr);
                            }} testid={`careers-val-${i}-label`} />
                            <button onClick={() => set("careers.values", data.careers.values.filter((_, idx) => idx !== i))} className="text-destructive text-[10px] tracked">
                                <Trash2 size={12} className="inline" /> Remove
                            </button>
                        </div>
                    ))}
                    <button onClick={() => set("careers.values", [...(data.careers?.values || []), { icon: "Coffee", label: "" }])} className="inline-flex items-center gap-2 tracked text-[10px] text-coffee gold-underline">
                        <Plus size={14} /> Add Value
                    </button>
                </div>

                <div className="border-t border-coffee/10 pt-6 mt-6">
                    <div className="tracked text-coffee/60 text-[10px] mb-4">Open Roles</div>
                    {data.careers?.roles?.map((r, i) => (
                        <div key={i} className="p-4 border border-coffee/10 mb-4 space-y-3">
                            <TextField label="Title" value={r.title} onChange={(v) => {
                                const arr = [...data.careers.roles]; arr[i] = { ...arr[i], title: v };
                                set("careers.roles", arr);
                            }} testid={`careers-role-${i}-title`} />
                            <TextField label="City / Location" value={r.city} onChange={(v) => {
                                const arr = [...data.careers.roles]; arr[i] = { ...arr[i], city: v };
                                set("careers.roles", arr);
                            }} testid={`careers-role-${i}-city`} />
                            <TextField label="Type" value={r.type} onChange={(v) => {
                                const arr = [...data.careers.roles]; arr[i] = { ...arr[i], type: v };
                                set("careers.roles", arr);
                            }} testid={`careers-role-${i}-type`} />
                            <button onClick={() => set("careers.roles", data.careers.roles.filter((_, idx) => idx !== i))} className="text-destructive text-[10px] tracked">
                                <Trash2 size={12} className="inline" /> Remove
                            </button>
                        </div>
                    ))}
                    <button onClick={() => set("careers.roles", [...(data.careers?.roles || []), { title: "", city: "", type: "Full-time" }])} className="inline-flex items-center gap-2 tracked text-[10px] text-coffee gold-underline">
                        <Plus size={14} /> Add Role
                    </button>
                </div>
            </SectionCard>

            <SectionCard title="Our Cafes Page & Outlets">
                <TextField label="Hero Eyebrow Label" value={data.our_cafes?.label} onChange={(v) => set("our_cafes.label", v)} testid="cafes-label" />
                <TextField label="Hero Title" value={data.our_cafes?.title} onChange={(v) => set("our_cafes.title", v)} testid="cafes-title-input" />
                <TextArea label="Hero Subtitle" value={data.our_cafes?.subtitle} onChange={(v) => set("our_cafes.subtitle", v)} testid="cafes-subtitle" />
                <ImagePicker label="Hero Background Image" value={data.our_cafes?.hero_image} onChange={(v) => set("our_cafes.hero_image", v)} testid="cafes-hero-image" hint="Recommended: 1920 × 1080 px · JPG · under 800 KB" />

                <div className="border-t border-coffee/10 pt-6 mt-2">
                    <TextField label="Map Heading" value={data.our_cafes?.map_heading} onChange={(v) => set("our_cafes.map_heading", v)} testid="cafes-map-heading" />
                    <TextArea label="Google Maps Embed URL" value={data.our_cafes?.map_embed_url} onChange={(v) => set("our_cafes.map_embed_url", v)} testid="cafes-map-embed" />
                    <div className="font-mono text-[10px] text-coffee/50 -mt-3 mb-4">
                        Tip: On Google Maps → Share → Embed a map → copy the <code>src</code> URL only (the one inside the iframe).
                    </div>
                </div>

                <div className="border-t border-coffee/10 pt-6">
                    <div className="tracked text-coffee/60 text-[10px] mb-4">Expanding Soon Section</div>
                    <TextField label="Heading" value={data.our_cafes?.expanding?.heading} onChange={(v) => set("our_cafes.expanding.heading", v)} testid="cafes-exp-heading" />
                    <TextArea label="Body" value={data.our_cafes?.expanding?.body} onChange={(v) => set("our_cafes.expanding.body", v)} testid="cafes-exp-body" />
                    <TextField label="Primary CTA Label" value={data.our_cafes?.expanding?.primary_label} onChange={(v) => set("our_cafes.expanding.primary_label", v)} testid="cafes-exp-primary" />
                    <TextField label="Primary CTA Link" value={data.our_cafes?.expanding?.primary_link} onChange={(v) => set("our_cafes.expanding.primary_link", v)} testid="cafes-exp-primary-link" />
                    <TextField label="Secondary CTA Label" value={data.our_cafes?.expanding?.secondary_label} onChange={(v) => set("our_cafes.expanding.secondary_label", v)} testid="cafes-exp-secondary" />
                </div>

                <div className="border-t border-coffee/10 pt-6 mt-6">
                    <div className="tracked text-coffee/60 text-[10px] mb-4">
                        Outlets · vibe tags: Airport, Dine-In, Work-Friendly, Quick Grab, Cosy Corner, Flagship · image: 1200 × 900 px · max 2 MB
                    </div>
                    {(data.our_cafes?.cafes || []).map((cafe, i) => (
                        <div key={i} className="p-4 border border-coffee/10 mb-4 space-y-3">
                            <div className="flex items-center justify-between gap-3 flex-wrap">
                                <div className="font-mono tracked text-coffee text-[11px]">Outlet {i + 1}</div>
                                <div className="flex items-center gap-4">
                                    <label className="inline-flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={!!cafe.featured} onChange={(e) => {
                                            const arr = [...data.our_cafes.cafes]; arr[i] = { ...arr[i], featured: e.target.checked };
                                            set("our_cafes.cafes", arr);
                                        }} className="accent-coffee" data-testid={`cafe-${i}-featured`} />
                                        <span className="tracked text-[10px] text-coffee/70">Featured</span>
                                    </label>
                                    <label className="inline-flex items-center gap-2 cursor-pointer">
                                        <input type="checkbox" checked={cafe.active !== false} onChange={(e) => {
                                            const arr = [...data.our_cafes.cafes]; arr[i] = { ...arr[i], active: e.target.checked };
                                            set("our_cafes.cafes", arr);
                                        }} className="accent-coffee" data-testid={`cafe-${i}-active`} />
                                        <span className="tracked text-[10px] text-coffee/70">Active</span>
                                    </label>
                                </div>
                            </div>
                            <TextField label="Slug / ID" value={cafe.id} onChange={(v) => {
                                const arr = [...data.our_cafes.cafes]; arr[i] = { ...arr[i], id: v };
                                set("our_cafes.cafes", arr);
                            }} testid={`cafe-${i}-id`} />
                            <TextField label="Name" value={cafe.name} onChange={(v) => {
                                const arr = [...data.our_cafes.cafes]; arr[i] = { ...arr[i], name: v };
                                set("our_cafes.cafes", arr);
                            }} testid={`cafe-${i}-name`} />
                            <TextField label="Area / Address" value={cafe.area} onChange={(v) => {
                                const arr = [...data.our_cafes.cafes]; arr[i] = { ...arr[i], area: v };
                                set("our_cafes.cafes", arr);
                            }} testid={`cafe-${i}-area`} />
                            <ImagePicker label="Photo" value={cafe.image} onChange={(v) => {
                                const arr = [...data.our_cafes.cafes]; arr[i] = { ...arr[i], image: v };
                                set("our_cafes.cafes", arr);
                            }} testid={`cafe-${i}-image`} hint="1200 × 900 px · max 2 MB" />
                            <TextArea label="Character Note" value={cafe.character_note} onChange={(v) => {
                                const arr = [...data.our_cafes.cafes]; arr[i] = { ...arr[i], character_note: v };
                                set("our_cafes.cafes", arr);
                            }} testid={`cafe-${i}-note`} />
                            <TextField label="Hours (e.g., 8 AM — 11 PM)" value={cafe.hours} onChange={(v) => {
                                const arr = [...data.our_cafes.cafes]; arr[i] = { ...arr[i], hours: v };
                                set("our_cafes.cafes", arr);
                            }} testid={`cafe-${i}-hours`} />
                            <TextField label="Vibe Tags (comma-separated)" value={(cafe.vibe_tags || []).join(", ")} onChange={(v) => {
                                const tags = v.split(",").map((s) => s.trim()).filter(Boolean);
                                const arr = [...data.our_cafes.cafes]; arr[i] = { ...arr[i], vibe_tags: tags };
                                set("our_cafes.cafes", arr);
                            }} testid={`cafe-${i}-tags`} />
                            <TextField label="Google Maps URL" value={cafe.maps_url} onChange={(v) => {
                                const arr = [...data.our_cafes.cafes]; arr[i] = { ...arr[i], maps_url: v };
                                set("our_cafes.cafes", arr);
                            }} testid={`cafe-${i}-maps`} />
                            <TextField label="Phone Number" value={cafe.phone} onChange={(v) => {
                                const arr = [...data.our_cafes.cafes]; arr[i] = { ...arr[i], phone: v };
                                set("our_cafes.cafes", arr);
                            }} testid={`cafe-${i}-phone`} />
                            <button onClick={() => set("our_cafes.cafes", data.our_cafes.cafes.filter((_, idx) => idx !== i))} className="text-destructive text-[10px] tracked" data-testid={`cafe-${i}-remove`}>
                                <Trash2 size={12} className="inline" /> Remove Outlet
                            </button>
                        </div>
                    ))}
                    <button
                        onClick={() => set("our_cafes.cafes", [...(data.our_cafes?.cafes || []), {
                            id: `new-${Date.now()}`, name: "New Outlet", area: "",
                            image: "", character_note: "", hours: "8 AM — 10 PM",
                            vibe_tags: ["Dine-In"], maps_url: "", phone: "", featured: false, active: true,
                        }])}
                        className="inline-flex items-center gap-2 tracked text-[10px] text-coffee gold-underline"
                        data-testid="cafes-add-outlet"
                    >
                        <Plus size={14} /> Add Outlet
                    </button>
                </div>
            </SectionCard>

            <SaveBar onSave={save} saving={saving} label="Save Pages" />
        </div>
    );
}
